import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { adminAPI, microsoftAuthAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { getCurrencyOptions, DEFAULT_CURRENCY } from '../../lib/currency';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Settings, DollarSign, Globe, Bell, Shield, Database, 
  Save, RefreshCw, Building2, Users, Palette, KeyRound,
  CheckCircle2, XCircle, Eye, EyeOff, ExternalLink, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
  return { Authorization: `Bearer ${token}` };
};

export function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const { setCurrency: setGlobalCurrency } = useCurrency();
  
  // Settings state
  const [settings, setSettings] = useState({
    // General
    company_name: 'Securado',
    default_currency: DEFAULT_CURRENCY,
    timezone: 'UTC',
    date_format: 'MM/DD/YYYY',
    
    // Notifications
    email_notifications: true,
    deal_alerts: true,
    activity_reminders: true,
    weekly_reports: false,
    
    // Integration defaults
    default_sync_interval: '15',
    auto_map_fields: true,
    enable_data_validation: true,
    
    // Security
    session_timeout: '60',
    require_2fa: false,
    password_expiry_days: '90',

    // Bluesheet
    bluesheet_settings: {
      email_notifications_enabled: false,
      weights: {
        stage_score: 0.2,
        buying_influences: 0.3,
        competition_status: 0.1,
        timeline_alignment: 0.1,
        budget_status: 0.15,
        activity_engagement: 0.15,
      },
      role_profiles: {},
    },
  });

  // Microsoft SSO state
  const [ssoConfig, setSsoConfig] = useState({
    client_id: '',
    tenant_id: '',
    redirect_uri: '',
    client_secret: '',
    client_secret_masked: '',
    sender_mailbox: '',
    mail_notifications_enabled: false,
  });
  const [ssoStatus, setSsoStatus] = useState({ configured: false });
  const [ssoSaving, setSsoSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [mailTestRecipient, setMailTestRecipient] = useState('');
  const [mailTesting, setMailTesting] = useState(false);

  // AI / LLM state
  const [aiSettings, setAiSettings] = useState({
    llm_api_key: '',
    llm_provider: 'emergent',
    has_llm_key: false,
    company_name: '',
    odoo_url: '',
    odoo_database: '',
    odoo_username: '',
    odoo_api_key: '',
  });
  const [aiSaving, setAiSaving] = useState(false);
  const [showLlmKey, setShowLlmKey] = useState(false);

  useEffect(() => {
    loadSettings();
    loadSsoConfig();
    loadAiSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Try to load settings from backend
      const res = await adminAPI.getSettings();
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      // Use defaults if no settings exist
    } finally {
      setLoading(false);
    }
  };

  const loadSsoConfig = async () => {
    try {
      const [configRes, statusRes] = await Promise.all([
        microsoftAuthAPI.getAdminConfig(),
        microsoftAuthAPI.getStatus()
      ]);
      
      if (configRes.data?.settings) {
        setSsoConfig(prev => ({ ...prev, ...configRes.data.settings }));
      }
      if (statusRes.data) {
        setSsoStatus(statusRes.data);
      }
    } catch (error) {
    }
  };

  const loadAiSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/app-settings`, { headers: getAuthHeader() });
      const d = res.data || {};
      setAiSettings(prev => ({
        ...prev,
        llm_provider: d.llm_provider || 'emergent',
        llm_custom_url: d.llm_custom_url || '',
        llm_model: d.llm_model || '',
        has_llm_key: d.has_llm_key || false,
        company_name: d.company_name || '',
        odoo_url: d.odoo_connection?.url || '',
        odoo_database: d.odoo_connection?.database || '',
        odoo_username: d.odoo_connection?.username || '',
      }));
    } catch { /* settings not yet configured */ }
  };

  const handleSaveAiSettings = async () => {
    setAiSaving(true);
    try {
      const payload = {
        llm_provider: aiSettings.llm_provider,
        company_name: aiSettings.company_name,
      };
      if (aiSettings.llm_api_key) payload.llm_api_key = aiSettings.llm_api_key;
      if (aiSettings.llm_custom_url) payload.llm_custom_url = aiSettings.llm_custom_url;
      if (aiSettings.llm_model) payload.llm_model = aiSettings.llm_model;
      if (aiSettings.odoo_api_key) payload.odoo_api_key = aiSettings.odoo_api_key;
      if (aiSettings.odoo_url) payload.odoo_url = aiSettings.odoo_url;
      if (aiSettings.odoo_database) payload.odoo_database = aiSettings.odoo_database;
      if (aiSettings.odoo_username) payload.odoo_username = aiSettings.odoo_username;
      
      await axios.put(`${API_URL}/api/app-settings`, payload, { headers: getAuthHeader() });
      toast.success('AI & Integration settings saved!');
      setAiSettings(prev => ({ ...prev, llm_api_key: '', odoo_api_key: '' }));
      loadAiSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setAiSaving(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      // Update global currency context when settings are saved
      if (settings.default_currency) {
        setGlobalCurrency(settings.default_currency);
      }
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    // Immediately update global currency when it changes
    if (key === 'default_currency') {
      setGlobalCurrency(value);
    }
  };
  const updateBluesheetSetting = (key, value) => {
    setSettings(prev => ({ ...prev, bluesheet_settings: { ...(prev.bluesheet_settings || {}), [key]: value } }));
  };
  const updateBluesheetWeight = (key, value) => {
    const num = Number(value);
    setSettings(prev => ({
      ...prev,
      bluesheet_settings: {
        ...(prev.bluesheet_settings || {}),
        weights: { ...(prev.bluesheet_settings?.weights || {}), [key]: Number.isFinite(num) ? num : value },
      },
    }));
  };
  const updateBluesheetRoleWeight = (role, key, value) => {
    const num = Number(value);
    setSettings(prev => ({
      ...prev,
      bluesheet_settings: {
        ...(prev.bluesheet_settings || {}),
        role_profiles: {
          ...(prev.bluesheet_settings?.role_profiles || {}),
          [role]: {
            ...((prev.bluesheet_settings?.role_profiles || {})[role] || prev.bluesheet_settings?.weights || {}),
            [key]: Number.isFinite(num) ? num : value,
          },
        },
      },
    }));
  };

  const updateSsoConfig = (key, value) => {
    setSsoConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSso = async () => {
    setSsoSaving(true);
    try {
      const res = await microsoftAuthAPI.saveAdminConfig(ssoConfig);
      if (res.data?.success) {
        toast.success('Microsoft SSO configuration saved');
        setSsoStatus({ configured: res.data.configured });
        // Reload config to get masked secret
        await loadSsoConfig();
      }
    } catch (error) {
      toast.error('Failed to save SSO configuration');
    } finally {
      setSsoSaving(false);
    }
  };

  const handleSendTestMail = async () => {
    if (!mailTestRecipient.trim()) {
      toast.error('Enter a test recipient email');
      return;
    }
    setMailTesting(true);
    try {
      await microsoftAuthAPI.sendTestMail(mailTestRecipient.trim());
      toast.success('Test email sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setMailTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground">Configure system preferences and defaults</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-primary hover:bg-primary/90"
          data-testid="save-settings-btn"
        >
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="general" data-testid="tab-general">
            <Settings className="h-4 w-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="currency" data-testid="tab-currency">
            <DollarSign className="h-4 w-4 mr-2" />
            Currency
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="integration" data-testid="tab-integration">
            <Database className="h-4 w-4 mr-2" />
            Integration
          </TabsTrigger>
          <TabsTrigger value="sso" data-testid="tab-sso">
            <KeyRound className="h-4 w-4 mr-2" />
            SSO
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <Zap className="h-4 w-4 mr-2" />
            AI & Integrations
          </TabsTrigger>
          <TabsTrigger value="bluesheet" data-testid="tab-bluesheet">
            <Shield className="h-4 w-4 mr-2" />
            Bluesheet
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Organization
              </CardTitle>
              <CardDescription>Basic organization settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={settings.company_name}
                    onChange={(e) => updateSetting('company_name', e.target.value)}
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={settings.timezone} onValueChange={(v) => updateSetting('timezone', v)}>
                    <SelectTrigger data-testid="timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                      <SelectItem value="Europe/London">London</SelectItem>
                      <SelectItem value="Asia/Dubai">Dubai (GST)</SelectItem>
                      <SelectItem value="Asia/Muscat">Muscat (GST)</SelectItem>
                      <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_format">Date Format</Label>
                <Select value={settings.date_format} onValueChange={(v) => updateSetting('date_format', v)}>
                  <SelectTrigger className="w-[200px]" data-testid="date-format-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                    <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                    <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="currency" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Currency Settings
              </CardTitle>
              <CardDescription>Configure default currency for the organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="default_currency">Default Currency</Label>
                <Select 
                  value={settings.default_currency} 
                  onValueChange={(v) => updateSetting('default_currency', v)}
                >
                  <SelectTrigger className="w-[300px]" data-testid="default-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getCurrencyOptions().map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{curr.symbol}</span>
                          <span>{curr.code}</span>
                          <span className="text-muted-foreground">- {curr.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  This currency will be used as the default for all new invoices, opportunities, and reports.
                </p>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium mb-3">Supported Currencies</h4>
                <div className="grid grid-cols-4 gap-3">
                  {getCurrencyOptions().map((curr) => (
                    <div 
                      key={curr.code}
                      className={`p-3 rounded-lg border ${
                        settings.default_currency === curr.code 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-mono">{curr.symbol}</span>
                        <div>
                          <div className="font-medium text-sm">{curr.code}</div>
                          <div className="text-xs text-muted-foreground">{curr.name}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Notification Preferences
              </CardTitle>
              <CardDescription>Configure system-wide notification defaults</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">Send email notifications for important events</p>
                </div>
                <Switch 
                  checked={settings.email_notifications} 
                  onCheckedChange={(v) => updateSetting('email_notifications', v)}
                  data-testid="toggle-email-notifications"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Deal Alerts</p>
                  <p className="text-sm text-muted-foreground">Notify when deals change stage or are at risk</p>
                </div>
                <Switch 
                  checked={settings.deal_alerts} 
                  onCheckedChange={(v) => updateSetting('deal_alerts', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Activity Reminders</p>
                  <p className="text-sm text-muted-foreground">Send reminders for upcoming activities</p>
                </div>
                <Switch 
                  checked={settings.activity_reminders} 
                  onCheckedChange={(v) => updateSetting('activity_reminders', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Weekly Reports</p>
                  <p className="text-sm text-muted-foreground">Send weekly performance summary to all users</p>
                </div>
                <Switch 
                  checked={settings.weekly_reports} 
                  onCheckedChange={(v) => updateSetting('weekly_reports', v)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integration" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="h-4 w-4" />
                Integration Defaults
              </CardTitle>
              <CardDescription>Configure default settings for data integrations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Sync Interval</Label>
                  <Select 
                    value={settings.default_sync_interval} 
                    onValueChange={(v) => updateSetting('default_sync_interval', v)}
                  >
                    <SelectTrigger data-testid="sync-interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">Every 5 minutes</SelectItem>
                      <SelectItem value="15">Every 15 minutes</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                      <SelectItem value="60">Every hour</SelectItem>
                      <SelectItem value="1440">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Auto-Map Fields</p>
                  <p className="text-sm text-muted-foreground">Automatically suggest field mappings based on names</p>
                </div>
                <Switch 
                  checked={settings.auto_map_fields} 
                  onCheckedChange={(v) => updateSetting('auto_map_fields', v)}
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Enable Data Validation</p>
                  <p className="text-sm text-muted-foreground">Validate data before importing to canonical store</p>
                </div>
                <Switch 
                  checked={settings.enable_data_validation} 
                  onCheckedChange={(v) => updateSetting('enable_data_validation', v)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Default Entity Mappings
              </CardTitle>
              <CardDescription>Configure which source models map to which CRM entities by default</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Source Model Pattern</p>
                    <p className="font-medium">res.partner</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Maps To</p>
                    <Badge>Account / Contact</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                    <Badge variant="outline" className="text-emerald-600">Active</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Source Model Pattern</p>
                    <p className="font-medium">crm.lead</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Maps To</p>
                    <Badge>Opportunity</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                    <Badge variant="outline" className="text-emerald-600">Active</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Source Model Pattern</p>
                    <p className="font-medium">account.move</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Maps To</p>
                    <Badge>Invoice</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                    <Badge variant="outline" className="text-emerald-600">Active</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Source Model Pattern</p>
                    <p className="font-medium">hr.employee</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Maps To</p>
                    <Badge>User</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                    <Badge variant="outline" className="text-emerald-600">Active</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Source Model Pattern</p>
                    <p className="font-medium">sale.order</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Maps To</p>
                    <Badge>Order</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                    <Badge variant="outline" className="text-emerald-600">Active</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SSO Configuration Tab */}
        <TabsContent value="sso" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    Microsoft SSO Configuration
                  </CardTitle>
                  <CardDescription>
                    Configure Azure AD / Microsoft Entra ID for Single Sign-On
                  </CardDescription>
                </div>
                <Badge 
                  variant={ssoStatus.configured ? "default" : "secondary"}
                  className={ssoStatus.configured ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}
                >
                  {ssoStatus.configured ? (
                    <><CheckCircle2 className="h-3 w-3 mr-1" /> Configured</>
                  ) : (
                    <><XCircle className="h-3 w-3 mr-1" /> Not Configured</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Setup Instructions */}
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <AlertDescription className="text-sm">
                  <strong>Setup Instructions:</strong>
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline inline-flex items-center gap-1">Azure Portal <ExternalLink className="h-3 w-3" /></a> → Azure Active Directory → App registrations</li>
                    <li>Create a new registration for your application</li>
                    <li>Set the redirect URI to the value shown below</li>
                    <li>Copy the Application (Client) ID and Directory (Tenant) ID</li>
                    <li>Create a Client Secret under "Certificates & secrets"</li>
                    <li>Enter all values below and save</li>
                  </ol>
                </AlertDescription>
              </Alert>

              <div className="grid gap-4">
                {/* Application (Client) ID */}
                <div className="space-y-2">
                  <Label htmlFor="client_id">Application (Client) ID</Label>
                  <Input
                    id="client_id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={ssoConfig.client_id}
                    onChange={(e) => updateSsoConfig('client_id', e.target.value)}
                    data-testid="sso-client-id-input"
                  />
                  <p className="text-xs text-muted-foreground">Found in Azure AD → App registration → Overview</p>
                </div>

                {/* Directory (Tenant) ID */}
                <div className="space-y-2">
                  <Label htmlFor="tenant_id">Directory (Tenant) ID</Label>
                  <Input
                    id="tenant_id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={ssoConfig.tenant_id}
                    onChange={(e) => updateSsoConfig('tenant_id', e.target.value)}
                    data-testid="sso-tenant-id-input"
                  />
                  <p className="text-xs text-muted-foreground">Found in Azure AD → Overview</p>
                </div>

                {/* Client Secret */}
                <div className="space-y-2">
                  <Label htmlFor="client_secret">Client Secret</Label>
                  <div className="relative">
                    <Input
                      id="client_secret"
                      type={showSecret ? "text" : "password"}
                      placeholder={ssoConfig.client_secret_masked || "Enter client secret"}
                      value={ssoConfig.client_secret}
                      onChange={(e) => updateSsoConfig('client_secret', e.target.value)}
                      data-testid="sso-client-secret-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Found in Azure AD → App registration → Certificates & secrets
                    {ssoConfig.client_secret_masked && (
                      <span className="ml-2 text-green-500">Current: {ssoConfig.client_secret_masked}</span>
                    )}
                  </p>
                </div>

                {/* Redirect URI */}
                <div className="space-y-2">
                  <Label htmlFor="redirect_uri">Redirect URI</Label>
                  <Input
                    id="redirect_uri"
                    placeholder="https://your-domain.com/api/auth/microsoft/callback"
                    value={ssoConfig.redirect_uri}
                    onChange={(e) => updateSsoConfig('redirect_uri', e.target.value)}
                    data-testid="sso-redirect-uri-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Must match exactly in Azure AD. Suggested: <code className="bg-muted px-1 rounded">{window.location.origin}/api/auth/microsoft/callback</code>
                  </p>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="sender_mailbox">Graph Sender Mailbox</Label>
                  <Input
                    id="sender_mailbox"
                    placeholder="noreply@company.com"
                    value={ssoConfig.sender_mailbox}
                    onChange={(e) => updateSsoConfig('sender_mailbox', e.target.value)}
                    data-testid="sso-sender-mailbox-input"
                  />
                  <p className="text-xs text-muted-foreground">This mailbox will send feedback replies and explanation/update notifications via Microsoft Graph.</p>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="space-y-1">
                    <p className="font-medium">Enable Graph Email Notifications</p>
                    <p className="text-sm text-muted-foreground">Uses the same Azure app as SSO. Requires Microsoft Graph application permission <code>Mail.Send</code> with admin consent.</p>
                  </div>
                  <Switch
                    checked={!!ssoConfig.mail_notifications_enabled}
                    onCheckedChange={(v) => updateSsoConfig('mail_notifications_enabled', v)}
                    data-testid="toggle-mail-notifications-enabled"
                  />
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                  <div>
                    <p className="font-medium text-sm">Test Graph Mail</p>
                    <p className="text-xs text-muted-foreground mt-1">Send a test message using the configured mailbox to confirm Graph `Mail.Send` is working.</p>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="recipient@company.com"
                      value={mailTestRecipient}
                      onChange={(e) => setMailTestRecipient(e.target.value)}
                      data-testid="sso-test-mail-recipient"
                    />
                    <Button type="button" variant="outline" onClick={handleSendTestMail} disabled={mailTesting} data-testid="sso-test-mail-btn">
                      {mailTesting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />} Test Send
                    </Button>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {ssoStatus.configured ? (
                    <div className="space-y-1">
                      <span className="text-green-500 flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" />
                        Microsoft SSO is active. Users can sign in with their Microsoft accounts.
                      </span>
                      <span className={`${ssoStatus.mail_configured ? 'text-green-500' : 'text-yellow-500'} flex items-center gap-1`}>
                      <CheckCircle2 className="h-4 w-4" />
                        {ssoStatus.mail_configured ? 'Graph mail is configured for feedback notifications.' : 'Graph mail is not fully configured yet (check sender mailbox + Mail.Send permission).'}
                      </span>
                    </div>
                  ) : (
                    <span className="text-yellow-500">
                      Complete the configuration above to enable Microsoft SSO.
                    </span>
                  )}
                </div>
                <Button 
                  onClick={handleSaveSso}
                  disabled={ssoSaving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-sso-config-btn"
                >
                  {ssoSaving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save SSO Configuration
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* RBAC Auto-Linking Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                RBAC Auto-Linking
              </CardTitle>
              <CardDescription>
                How Microsoft users are linked to Odoo RBAC profiles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">1</div>
                  <div>
                    <p className="font-medium">User signs in with Microsoft</p>
                    <p className="text-muted-foreground">User clicks "Sign in with Microsoft" and authenticates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">2</div>
                  <div>
                    <p className="font-medium">Email matching</p>
                    <p className="text-muted-foreground">System searches for a matching email in the synced RBAC users</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">3</div>
                  <div>
                    <p className="font-medium">Access level assigned</p>
                    <p className="text-muted-foreground">
                      If matched → User gets Odoo permissions (ADMIN/MANAGER/USER)<br/>
                      If not matched → User gets RESTRICTED access with a warning
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-500">
                  <strong>Tip:</strong> Make sure to sync RBAC from Odoo (Admin → RBAC Sync) before enabling Microsoft SSO, 
                  so users' emails can be matched to their Odoo permissions.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI & Integrations Tab — CSS matches other settings tabs */}
        <TabsContent value="bluesheet" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> Bluesheet Scoring</CardTitle>
              <CardDescription>Control scoring weights, role profiles, and recalculation notifications for Bluesheet probability.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-1">
                  <p className="font-medium">Email notifications on Bluesheet recalculation</p>
                  <p className="text-sm text-muted-foreground">Uses the Graph sender mailbox configured in the SSO tab.</p>
                </div>
                <Switch checked={!!settings.bluesheet_settings?.email_notifications_enabled} onCheckedChange={(v) => updateBluesheetSetting('email_notifications_enabled', v)} data-testid="toggle-bluesheet-email-notifications" />
              </div>

              <div>
                <h3 className="font-medium mb-3">Default Section Weights</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(settings.bluesheet_settings?.weights || {}).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input type="number" step="0.01" value={value} onChange={(e) => updateBluesheetWeight(key, e.target.value)} data-testid={`bluesheet-weight-${key}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-3">Role Profiles</h3>
                <div className="space-y-4">
                  {['sales_rep', 'sales_director', 'product_director', 'finance_manager'].map((role) => (
                    <div key={role} className="rounded-lg border p-4 space-y-3">
                      <p className="font-medium capitalize">{role.replace(/_/g, ' ')}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(settings.bluesheet_settings?.role_profiles?.[role] || settings.bluesheet_settings?.weights || {}).map(([key, value]) => (
                          <div key={key} className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</Label>
                            <Input type="number" step="0.01" value={value} onChange={(e) => updateBluesheetRoleWeight(role, key, e.target.value)} data-testid={`bluesheet-role-${role}-${key}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="mt-6 space-y-6">
          
          {/* AI Configuration */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">AI & LLM Configuration</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Configure your AI language model. Supports Emergent Universal Key, OpenAI, Anthropic, or any custom endpoint.
              </p>
            </div>
            <Button onClick={handleSaveAiSettings} disabled={aiSaving}
              className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-ai-settings">
              {aiSaving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* LLM Provider Card */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  LLM Provider
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiSettings.has_llm_key && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded-md border border-green-200 text-sm text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    API key is configured
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="llm-provider">Provider</Label>
                  <Select value={aiSettings.llm_provider} onValueChange={v => setAiSettings(f => ({...f, llm_provider: v}))}>
                    <SelectTrigger id="llm-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emergent">Emergent Universal Key (GPT-4 / Claude / Gemini)</SelectItem>
                      <SelectItem value="openai">OpenAI (direct)</SelectItem>
                      <SelectItem value="anthropic">Anthropic Claude (direct)</SelectItem>
                      <SelectItem value="custom">Custom / Self-hosted (OpenAI-compatible)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Emergent Universal Key works with GPT-4, Claude, and Gemini using a single key.
                    Get it at <a href="https://app.emergentlabs.ai" target="_blank" rel="noreferrer" className="underline">app.emergentlabs.ai</a>
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      API Key
                      <Badge variant="outline" className="text-[10px]">Encrypted</Badge>
                    </span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="llm-api-key"
                      type={showLlmKey ? 'text' : 'password'}
                      value={aiSettings.llm_api_key}
                      onChange={e => setAiSettings(f => ({...f, llm_api_key: e.target.value}))}
                      placeholder={aiSettings.has_llm_key ? "Enter new key to replace existing..." : "sk-... or your API key"}
                      className="pr-10"
                      data-testid="llm-api-key-input"
                    />
                    <button type="button" onClick={() => setShowLlmKey(!showLlmKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showLlmKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Custom LLM URL — shown for custom/self-hosted */}
                {aiSettings.llm_provider === 'custom' && (
                  <div className="space-y-2">
                    <Label htmlFor="llm-url">Custom API URL</Label>
                    <Input
                      id="llm-url"
                      type="url"
                      value={aiSettings.llm_custom_url || ''}
                      onChange={e => setAiSettings(f => ({...f, llm_custom_url: e.target.value}))}
                      placeholder="https://your-llm-endpoint.com/v1"
                    />
                    <Label htmlFor="llm-model">Model Name</Label>
                    <Input
                      id="llm-model"
                      value={aiSettings.llm_model || ''}
                      onChange={e => setAiSettings(f => ({...f, llm_model: e.target.value}))}
                      placeholder="gpt-4, llama-3, etc."
                    />
                    <p className="text-xs text-muted-foreground">
                      Compatible with any OpenAI-format API (Ollama, vLLM, Azure OpenAI, etc.)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Company Config Card */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  Company Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    value={aiSettings.company_name}
                    onChange={e => setAiSettings(f => ({...f, company_name: e.target.value}))}
                    placeholder="Your Company Ltd"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Separator />
          
          {/* Odoo Connection Section */}
          <div>
            <h3 className="text-base font-semibold mb-1">Odoo ERP Connection</h3>
            <p className="text-sm text-muted-foreground mb-4">Update your Odoo connection. Leave fields blank to keep existing values.</p>
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Odoo URL</Label>
                  <Input value={aiSettings.odoo_url}
                    onChange={e => setAiSettings(f => ({...f, odoo_url: e.target.value}))}
                    placeholder="https://erp.yourcompany.com" />
                </div>
                <div className="space-y-2">
                  <Label>Database Name</Label>
                  <Input value={aiSettings.odoo_database}
                    onChange={e => setAiSettings(f => ({...f, odoo_database: e.target.value}))}
                    placeholder="mycompany_db" />
                </div>
                <div className="space-y-2">
                  <Label>Username / Email</Label>
                  <Input value={aiSettings.odoo_username}
                    onChange={e => setAiSettings(f => ({...f, odoo_username: e.target.value}))}
                    placeholder="admin@company.com" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    Odoo API Key
                    <Badge variant="outline" className="text-[10px]">Encrypted</Badge>
                  </Label>
                  <Input type="password" value={aiSettings.odoo_api_key}
                    onChange={e => setAiSettings(f => ({...f, odoo_api_key: e.target.value}))}
                    placeholder="Leave blank to keep current key" />
                </div>
              </div>
            </CardContent>
          </Card>
          
        </TabsContent>
      </Tabs>
    </div>
  );
}
